import {cn} from '@tempmee/core'
import {type HTMLAttributes, type ReactNode, forwardRef} from 'react'
import {Typography} from '../typography/Typography'
import {DataRowVariantsFactory} from './Variants'

export type DataRowOrientation = 'horizontal' | 'stacked'

export interface DataRowProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Left/top label. Rendered as `Typography variant="body2"` in `text-secondary`. */
  label: ReactNode
  /** Right/bottom value. Rendered as `Typography variant="body1"` in `text-primary`. */
  value?: ReactNode
  /**
   * Layout: label + value on one line (`horizontal`) or value beneath the
   * label (`stacked`). @default 'horizontal'
   */
  orientation?: DataRowOrientation
  /** Renders a bottom divider rule (`divider` token). @default true */
  divider?: boolean
  /** Optional element beside the label — e.g. an info icon or a small chip. */
  icon?: ReactNode
}

/**
 * DataRow (v3) — a label/value detail row.
 *
 * A GoTu atom for "key: value" detail panels (e.g. an "Additional information"
 * card: Date · Sat Sept 14 2025; Type ⓘ · Contractor). The label is a small
 * `body2` in `text-secondary`; the value is `body1` in `text-primary`. Use
 * `orientation` to switch between an inline row (`horizontal`) and a stacked
 * layout (`stacked`, better for long values), `divider` for the bottom rule,
 * and `icon` to place an element beside the label.
 */
export const DataRow = forwardRef<HTMLDivElement, DataRowProps>(
  ({className, label, value, orientation = 'horizontal', divider = true, icon, ...props}, ref) => {
    const isStacked = orientation === 'stacked'

    return (
      <div
        ref={ref}
        className={cn(DataRowVariantsFactory({orientation, divider}), className)}
        {...props}
      >
        <div className="flex shrink-0 items-center gap-1">
          <Typography variant="body2" as="span" className="text-text-secondary">
            {label}
          </Typography>
          {icon}
        </div>
        <Typography
          variant="body1"
          textAlign={isStacked ? undefined : 'right'}
          className={cn(isStacked ? 'w-full' : 'min-w-0 flex-1')}
        >
          {value}
        </Typography>
      </div>
    )
  },
)
DataRow.displayName = 'DataRow'
